/**
 * Runs canopy classification off the main thread.
 *
 * Why: every touch of the sun arc reclassifies the whole selection. At the
 * desktop cap of 250 trees -- about 520k points -- a nine-step scrub spent 85%
 * of its wall time in long tasks, the longest 170-190 ms, which is felt as the
 * slider sticking. The work itself is a pure function of (clouds, buildings,
 * sun), so it belongs in a worker; nothing here needs the DOM.
 *
 * ## Who owns the point clouds
 *
 * The worker does. `putCloud` hands over each cloud once as it is fetched, and
 * the loader keeps only a point count (lib/pointcloud.ts) -- so the coordinates
 * exist in exactly one place, as they did before, just not this one. Results
 * come back as transferred typed arrays, so the per-move path copies nothing.
 *
 * ## Coalescing
 *
 * One classification in flight at a time, and at most one waiting. Dragging the
 * sun arc produces far more requests than the worker can answer; queueing them
 * all would mean the map trailing the control by seconds. A superseded request
 * resolves with `null` and its caller drops it, the same discipline `autoAbort`
 * applies to point cloud fetches. Staleness is bounded to one classification --
 * the worker cannot be interrupted mid-loop without chunking it, which is only
 * worth doing if a single pass stops fitting inside a scrub step.
 */
import { buildCanopyBuffers, EMPTY_BUFFERS, type CanopyBuffers } from './canopy';
import type { SelectedBuilding, ShadeMode, ShadingModel } from './types';
import type { SunVector } from './shadow';

export interface ClassifyRequest {
  /** Trees to classify, by id. Their clouds must already be uploaded. */
  treeIds: number[];
  buildings: SelectedBuilding[];
  sun: SunVector;
  shadeMode: ShadeMode;
  /** Whether `buildings` is a user-chosen subset rather than the whole view. */
  focused: boolean;
  model: ShadingModel;
}

export type ToWorker =
  | { kind: 'cloud'; id: number; points: Float64Array }
  | ({ kind: 'classify'; seq: number } & ClassifyRequest);

export type FromWorker = { kind: 'result'; seq: number; buffers: CanopyBuffers };

type Waiting = {
  request: ClassifyRequest;
  resolve: (buffers: CanopyBuffers | null) => void;
};

class CanopyClassifier {
  private worker: Worker | null = null;
  private started = false;
  /** Clouds, when there is no worker to hold them (tests, or a failed start). */
  private readonly local = new Map<number, Float64Array>();
  private seq = 0;
  private inFlight: Waiting | null = null;
  private queued: Waiting | null = null;

  /**
   * Takes a tree's cloud. The array is cloned into the worker and forgotten
   * here, so the caller should not keep a reference either.
   */
  putCloud(id: number, points: Float64Array): void {
    const worker = this.start();
    if (worker) worker.postMessage({ kind: 'cloud', id, points } satisfies ToWorker);
    else this.local.set(id, points);
  }

  /**
   * Resolves with packed buffers, or `null` if a later request superseded this
   * one before it ran.
   */
  classify(request: ClassifyRequest): Promise<CanopyBuffers | null> {
    const worker = this.start();
    if (!worker) {
      const clouds = request.treeIds
        .map((id) => this.local.get(id))
        .filter((points): points is Float64Array => points !== undefined)
        .map((points) => ({ points }));
      return Promise.resolve(
        buildCanopyBuffers(
          clouds,
          request.buildings,
          request.sun,
          request.shadeMode,
          request.focused,
          request.model,
        ),
      );
    }

    return new Promise((resolve) => {
      if (this.inFlight) {
        // Only the newest waiting request is worth running; anything it
        // displaced describes a sun position nobody is looking at any more.
        this.queued?.resolve(null);
        this.queued = { request, resolve };
        return;
      }
      this.inFlight = { request, resolve };
      this.send(worker, request);
    });
  }

  private send(worker: Worker, request: ClassifyRequest): void {
    worker.postMessage({ kind: 'classify', seq: ++this.seq, ...request } satisfies ToWorker);
  }

  private start(): Worker | null {
    if (this.started) return this.worker;
    this.started = true;

    // No worker under vitest or SSR; classification runs inline instead, which
    // is also what keeps buildCanopyBuffers directly testable.
    if (typeof Worker === 'undefined') return null;

    try {
      this.worker = new Worker(new URL('./canopyWorker.ts', import.meta.url), {
        type: 'module',
      });
      this.worker.onmessage = (event: MessageEvent<FromWorker>) => {
        const { seq, buffers } = event.data;
        const waiting = this.inFlight;
        this.inFlight = null;
        // Only the current request's answer is wanted; an earlier one arriving
        // late describes a sun position that has already moved on.
        if (waiting && seq === this.seq) waiting.resolve(buffers);
        else waiting?.resolve(null);

        const next = this.queued;
        this.queued = null;
        if (next && this.worker) {
          this.inFlight = next;
          this.send(this.worker, next.request);
        }
      };
      this.worker.onerror = (event) => {
        // The clouds live in the worker, so there is nothing to fall back to.
        // Loud rather than quietly blank: this only happens on a real bug.
        console.error('Canopy worker failed', event.message ?? event);
        this.inFlight?.resolve({ ...EMPTY_BUFFERS });
        this.inFlight = null;
        this.queued?.resolve(null);
        this.queued = null;
      };
    } catch (err) {
      console.error('Canopy worker unavailable, classifying on the main thread', err);
      this.worker = null;
    }

    return this.worker;
  }
}

/** One per document: the worker holds the point clouds, so it must be shared. */
export const classifier = new CanopyClassifier();
